import React from "react"

import SmallLogoImageSrc from "assets/airtutors-logo-square.png"
import LogoImageSrc from "assets/airtutors_banner.png"
import axios from "axios"
import { NotificationContainer } from "react-notifications"
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Link as RouterLink,
  Switch,
  useLocation,
} from "react-router-dom"
import { Provider } from "urql"

import {
  Box,
  CSSReset,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  Image,
  Link,
  Stack,
  ThemeProvider,
  useDisclosure,
} from "@chakra-ui/core"
import { Global } from "@emotion/core"

import { gql, useClient, useQuery } from "hooks/urql"

import NavLink from "components/Pages/NavLink"
import AirClassrooms from "components/Pages/Teacher/AirClassrooms"
// import AirClassroomDetails from "components/Pages/Teacher/AirClassrooms/Details"
import TeacherAudioRoom from "components/Pages/Teacher/AirClassrooms/TeacherAudioRoom"
import EditPassword from "components/Pages/Teacher/EditPassword"
import EditProfile from "components/Pages/Teacher/EditProfile"
import ErrorBoundary from "components/Pages/Teacher/ErrorBoundary"
import SessionCloudroom from "components/Pages/Teacher/SessionCloudroom"
import SessionsIndex from "components/Pages/Teacher/SessionsIndex"
import StudyGroupsIndex from "components/Pages/Teacher/StudyGroupsIndex"
import theme from "components/Pages/theme"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import MessageThreadArchive from "./MessageThreads/Archive"
import MessageThreadDetails from "./MessageThreads/Details"
import MessageThreadsIndex from "./MessageThreads/Index"

import MessageCountDisplay from "./MessageCountDisplay"
import TeacherContext from "./TeacherContext"

import "react-notifications/lib/notifications.css"

const TeacherApp = () => {
  const client = useClient()
  const [result] = useQuery({ query: viewerQuery, client })

  const logout = () => {
    axios.delete("/users/sign_out").then(() => (window.location.href = "/"))
  }

  const { isOpen, onToggle, onClose } = useDisclosure()

  const navLinkProps = {
    p: 4,
    _hover: {
      bg: "blue.800",
      color: "white",
    },
    height: "full",
    display: "flex",
    alignItems: "center",
    onClick: e => e.currentTarget.blur(),
  }

  return (
    <ErrorBoundary name="topLevel">
      <Provider value={client}>
        <ThemeProvider theme={theme}>
          <CSSReset />
          <NotificationContainer />
          <Router basename="/teacher">
            <SetBodyBackgroundColor />
            <UrqlLoadingIndicator result={result}>
              {({ data: { viewer } }) => (
                <TeacherContext.Provider value={viewer}>
                  <Switch>
                    <Route path="/sessions/:id/cloudroom">
                      <SessionCloudroom />
                    </Route>
                    {viewer.hasAcrClasses ? (
                      <Route path="/air-classrooms/room/:audioRoomId">
                        <TeacherAudioRoom />
                      </Route>
                    ) : null}
                    <Route>
                      <Box pb="16">
                        <Box
                          as="header"
                          position="fixed"
                          height="16"
                          top="0"
                          left="0"
                          zIndex="sticky"
                          bg="white"
                          w="full"
                          boxShadow="sm"
                        >
                          <Flex
                            display={{ sm: "none", md: "flex" }}
                            justify="space-between"
                            height="full"
                          >
                            <Stack isInline spacing="0" alignItems="center">
                              <Link
                                as={RouterLink}
                                to="/"
                                {...navLinkProps}
                                padding="0"
                                display="flex"
                                alignItems="center"
                                px="4"
                                _hover={{
                                  bg: "inherit",
                                }}
                              >
                                <Image
                                  src={LogoImageSrc}
                                  alt="Airtutors Logo"
                                  height="12"
                                />
                              </Link>
                              <Link
                                as={NavLink}
                                to="/sessions"
                                {...navLinkProps}
                              >
                                Sessions
                              </Link>
                              <Link
                                as={NavLink}
                                to="/study-groups"
                                {...navLinkProps}
                              >
                                Classes
                              </Link>
                              {viewer.hasAcrClasses ? (
                                <Link
                                  as={NavLink}
                                  to="/air-classrooms"
                                  {...navLinkProps}
                                >
                                  Air Classrooms
                                </Link>
                              ) : null}
                              {viewer.messagingDisabled ? null : (
                                <Link
                                  as={NavLink}
                                  to="/messages"
                                  {...navLinkProps}
                                >
                                  <MessageCountDisplay
                                    initialCount={viewer.unreadMessageCount}
                                    viewerId={viewer.id}
                                  />
                                </Link>
                              )}
                            </Stack>
                            <Stack isInline spacing="0">
                              <Link
                                as={NavLink}
                                to="/profile"
                                {...navLinkProps}
                              >
                                Edit Profile
                              </Link>
                              <Link {...navLinkProps} onClick={logout}>
                                Logout
                              </Link>
                            </Stack>
                          </Flex>

                          <Flex
                            display={{ sm: "flex", md: "none" }}
                            justify="space-between"
                            height="full"
                          >
                            <Link
                              as={RouterLink}
                              to="/"
                              {...navLinkProps}
                              padding="0"
                              display="flex"
                              alignItems="center"
                              px="4"
                              _hover={{
                                bg: "inherit",
                              }}
                            >
                              <Image
                                src={SmallLogoImageSrc}
                                alt="Airtutors Logo"
                                height="12"
                              />
                            </Link>
                            <IconButton
                              aria-label="Navigation Menu"
                              icon="drag-handle"
                              variant="ghost"
                              onClick={onToggle}
                              height="full"
                            />
                            <Drawer
                              size="xs"
                              isOpen={isOpen}
                              placement="left"
                              onClose={onClose}
                            >
                              <DrawerOverlay />
                              <DrawerContent>
                                <DrawerBody p={0}>
                                  <Box contentHeight="100vh" top="0">
                                    <Stack>
                                      <Link
                                        as={NavLink}
                                        to="/sessions"
                                        {...navLinkProps}
                                      >
                                        Sessions
                                      </Link>
                                      <Link
                                        as={NavLink}
                                        to="/study-groups"
                                        {...navLinkProps}
                                      >
                                        Classes
                                      </Link>
                                      {viewer.hasAcrClasses ? (
                                        <Link
                                          as={NavLink}
                                          to="/air-classrooms"
                                          {...navLinkProps}
                                        >
                                          Air Classrooms
                                        </Link>
                                      ) : null}
                                      {viewer.messagingDisabled ? null : (
                                        <Link
                                          as={NavLink}
                                          to="/messages"
                                          {...navLinkProps}
                                        >
                                          <MessageCountDisplay
                                            initialCount={
                                              viewer.unreadMessageCount
                                            }
                                            viewerId={viewer.id}
                                          />
                                        </Link>
                                      )}
                                      <Link
                                        as={NavLink}
                                        to="/profile"
                                        {...navLinkProps}
                                      >
                                        Edit Profile
                                      </Link>
                                      <Link {...navLinkProps} onClick={logout}>
                                        Logout
                                      </Link>
                                    </Stack>
                                  </Box>
                                </DrawerBody>
                              </DrawerContent>
                            </Drawer>
                          </Flex>
                        </Box>

                        <Box mx={["8", "2", "8"]} mt="16" pt="4">
                          <Switch>
                            <Route exact path="/sessions">
                              <SessionsIndex />
                            </Route>
                            <Route path="/sessions/semesters/:semesterId">
                              <SessionsIndex />
                            </Route>
                            <Route path="/study-groups">
                              <StudyGroupsIndex />
                            </Route>
                            {viewer.hasAcrClasses
                              ? [
                                  // <Route
                                  //   path="/air-classrooms/:id/details"
                                  //   key="air-classroom-details"
                                  // >
                                  //   <AirClassroomDetails />
                                  // </Route>,
                                  <Route
                                    path="/air-classrooms"
                                    key="air-classrooms"
                                  >
                                    <AirClassrooms />
                                  </Route>,
                                ]
                              : null}
                            {viewer.messagingDisabled
                              ? null
                              : [
                                  <Route
                                    path="/messages/:threadId(\d+)/archive"
                                    key="message-archive"
                                  >
                                    <MessageThreadArchive />
                                  </Route>,
                                  <Route
                                    path="/messages/:threadId(\d+)"
                                    key="message-thread-details"
                                  >
                                    <MessageThreadDetails />
                                  </Route>,
                                  <Route
                                    exact
                                    path="/messages"
                                    key="message-thread-index"
                                  >
                                    <MessageThreadsIndex />
                                  </Route>,
                                ]}
                            <Route path="/profile">
                              <EditProfile />
                            </Route>
                            <Route path="/password">
                              <EditPassword />
                            </Route>
                            <Route>
                              <Redirect
                                to={
                                  viewer.hasAcrClasses
                                    ? "/air-classrooms"
                                    : "/sessions"
                                }
                              />
                            </Route>
                          </Switch>
                        </Box>
                      </Box>
                    </Route>
                  </Switch>
                </TeacherContext.Provider>
              )}
            </UrqlLoadingIndicator>
          </Router>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  )
}

const SetBodyBackgroundColor = () => {
  const { pathname } = useLocation()
  const hasDarkBackground =
    /^\/(?:air-classrooms\/room\/[^/]+|sessions\/[^/]+\/cloudroom)\/?$/.test(
      pathname
    )

  return (
    <Global
      styles={{
        body: {
          backgroundColor: hasDarkBackground ? "#111" : "#fafafa",
        },
      }}
    />
  )
}

const viewerQuery = gql`
  query Viewer {
    viewer {
      id
      createdAt
      ssoEmail
      unreadMessageCount
      messagingDisabled
      hasAcrClasses
    }
  }
`

export default TeacherApp
